/**
 * Renderer-only tracking of "design docs changed since screens were last
 * generated" — drives the Screens group's "Generate from design" stale dot.
 *
 * Sections carry no timestamps in the design schema, so the comparison is two
 * per-game localStorage marks: the last doc/screen-affecting edit vs the last
 * successful generation. Per-machine durability is deliberate (same trade-off
 * as `components/scaffold-first-storage.ts`): the dot is a soft nudge, not a
 * source of truth, and a fresh machine simply starts un-stale.
 */

const GENERATED_KEY_PREFIX = 'crucible.design.screensGeneratedAt.';
const DOCS_CHANGED_KEY_PREFIX = 'crucible.design.docsChangedAt.';

/** Best-effort access to `localStorage` (absent under SSR / privacy modes). */
function getStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function readMs(key: string): number | null {
  const store = getStore();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (raw === null) return null;
    const ms = Number(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

function writeMs(key: string, ms: number): void {
  const store = getStore();
  if (!store) return;
  try {
    store.setItem(key, String(ms));
  } catch {
    /* quota exceeded / denied — best-effort, the dot just stays as-is */
  }
}

/** Record a successful screens generation (clears staleness). */
export function markScreensGenerated(gameId: string | undefined, now = Date.now()): void {
  if (!gameId) return;
  writeMs(GENERATED_KEY_PREFIX + gameId, now);
}

/** Record a design-doc edit (sets staleness once a generation exists). */
export function markDocsChanged(gameId: string | undefined, now = Date.now()): void {
  if (!gameId) return;
  writeMs(DOCS_CHANGED_KEY_PREFIX + gameId, now);
}

/**
 * Stale ⇔ docs changed after the last generation. Never generated → not stale
 * (the idle button already invites a first generation; a dot would be noise).
 */
export function isScreensStale(gameId: string | undefined): boolean {
  if (!gameId) return false;
  const generatedAt = readMs(GENERATED_KEY_PREFIX + gameId);
  if (generatedAt === null) return false;
  const changedAt = readMs(DOCS_CHANGED_KEY_PREFIX + gameId);
  return changedAt !== null && changedAt > generatedAt;
}
